import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getProducts,
  getProduct,
  createProduct,
  updateProduct,
  deleteProduct,
  getDeleteImpact,
  restoreProduct,
  uploadProducts,
  waitForUploadCompletion,
  uploadProductImage,
  importProductImageUrl,
} from './api';
import type { AsyncJob } from '../../services/asyncJobApi';
import type { ProductListParams, CreateProductRequest, UpdateProductRequest, BulkUploadResult, ImageSaveDestination } from './types';

/** 案件系クエリの親キー。useProduct / 一覧の無効化に使う (useUpdateProduct と同じ) */
export const PRODUCTS_QUERY_KEY = ['manual-products'];

/**
 * 商品一覧を取得するフック
 */
export function useProducts(params: ProductListParams = {}) {
  return useQuery({
    queryKey: [...PRODUCTS_QUERY_KEY, params],
    queryFn: () => getProducts(params),
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
  });
}

/**
 * 商品詳細を取得するフック
 */
export function useProduct(productId: string) {
  return useQuery({
    queryKey: [...PRODUCTS_QUERY_KEY, productId],
    queryFn: () => getProduct(productId),
    enabled: !!productId,
  });
}

/**
 * 商品を作成するフック
 */
export function useCreateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateProductRequest) => createProduct(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY });
    },
  });
}

/**
 * 商品を更新するフック
 */
export function useUpdateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProductRequest }) => updateProduct(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY });
    },
  });
}

/**
 * 商品を削除するフック
 */
export function useDeleteProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY });
    },
  });
}

/**
 * 商品削除の影響範囲を取得するフック (enabled false で待機可能)
 */
export function useDeleteImpact(productId: string | null | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...PRODUCTS_QUERY_KEY, productId, 'delete-impact'],
    queryFn: () => getDeleteImpact(productId!),
    enabled: Boolean(productId) && (options?.enabled ?? true),
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * 論理削除済の商品を復元するフック
 */
export function useRestoreProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => restoreProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY });
    },
  });
}

/**
 * 商品画像をアップロードするフック
 */
export function useUploadProductImage() {
  return useMutation({
    mutationFn: (file: File) => uploadProductImage(file),
  });
}

/** URL の画像を取り込んで案件画像にする (SVG は原本 + ラスタ版・§17) */
export function useImportProductImageUrl() {
  return useMutation({
    mutationFn: (url: string) => importProductImageUrl(url),
  });
}

/**
 * CSV/Excelアップロードフック（非同期ジョブ対応）
 *
 * 1. ファイルをアップロードしてジョブを作成
 * 2. ポーリングでジョブの完了を待機
 * 3. 完了結果を返す
 */
export function useUploadProducts(options?: {
  onProgress?: (job: AsyncJob) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, imageSaveDestination }: { file: File; imageSaveDestination?: ImageSaveDestination }): Promise<BulkUploadResult> => {
      const { job_id } = await uploadProducts(file, imageSaveDestination);
      return waitForUploadCompletion(job_id, {
        onProgress: options?.onProgress,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY });
    },
  });
}
